import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ButtonModel, ModalContext, Text } from '@notino/react-styleguide';
import { GetCatalogProductViewQuery } from '@notino/shared/definitions/types';

import { useTrackingContext } from '@context/tracking/TrackingContext';

import { useNewPdDesignEnabled } from '../../hooks/useNewPdDesignEnabled';

import { trackModalClose, trackModalOpen } from './gtm/tracking';
import { messages } from './messages';
import { ClickAndCollectModal } from './Modal';
import { messages as modalMessages } from './Modal/messages';
import { PickUpButton, PickUpButtonWrapper } from './styled';

type IClickAndCollectProps = {
  selectedVariant: GetCatalogProductViewQuery['productDetailByCatalogMasterId']['variants'][number];
  product: GetCatalogProductViewQuery['productDetailByCatalogMasterId'];
  onModalClosing: () => void;
  disabled?: boolean;
};

export const ClickAndCollect: React.FC<IClickAndCollectProps> = ({
  selectedVariant,
  product,
  onModalClosing,
  disabled = false,
}) => {
  const newDesign = useNewPdDesignEnabled();
  const { formatMessage } = useIntl();
  const { getTimeFromInit } = useTrackingContext();
  const { toggleModal, hideModal } = ModalContext.useModalContext();

  const toggleClickAndCollectModal = React.useCallback(() => {
    trackModalOpen({ timing: getTimeFromInit() });

    const handleModalCloseTracking = () => {
      hideModal();
      trackModalClose({ timing: getTimeFromInit() });
      onModalClosing();
    };

    const header = formatMessage(modalMessages.chooseStore);

    toggleModal(
      <ClickAndCollectModal
        selectedVariant={selectedVariant}
        product={product}
      />,
      {
        header: <Text typography="titleLarge">{header}</Text>,
        center: true,
        positionBottomOnMobile: true,
        withFocusTrap: true,
        onClose: handleModalCloseTracking,
      }
    );
  }, [
    getTimeFromInit,
    toggleModal,
    selectedVariant,
    product,
    formatMessage,
    hideModal,
    onModalClosing,
  ]);

  return (
    <PickUpButtonWrapper>
      <PickUpButton
        data-cypress="click-and-collect"
        newDesign={newDesign}
        fullWidth={true}
        buttonStyle={ButtonModel.Styles.secondary}
        onClick={toggleClickAndCollectModal}
        disabled={disabled}
      >
        <FormattedMessage {...messages.pickupNow} />
      </PickUpButton>
    </PickUpButtonWrapper>
  );
};
